<script setup lang="ts">
import { useGetArticleDetail } from '@/utils/apis'
import '@/assets/css/quill.snow.css'

const route = useRoute()
const { data: article } = await useGetArticleDetail(Number(route.params.id))

useSeoMeta({
  title: `${article.value?.title}-GlodaStory`,
  keywords: article.value?.tags?.map(item => item.name).join(','),
  description: article.value?.summary,
}, {
  tagPriority: 5,
})

function onPreOrNextPage(id: number) {
  navigateTo(`/content/detail/${id}`)
}
</script>

<template>
  <div mt-80px pb-24px>
    <section class="mx-auto w-1252px border-rd-16px bg-white px-56px py-24px">
      <template v-if="article">
        <BreadCrumb :append-paths="[{ name: article.title }]" />

        <h1 mt-24px text-24px font-600 class="text-[var(--title-text-color)]">
          {{ article.title }}
        </h1>

        <ElSpace :size="16" class="mt-4px text-12px text-[#999] font-400">
          <time>{{ article.createTime }}</time>
        </ElSpace>

        <ElSpace :size="24" fill class="mt-24px">
          <section class="article ql-editor" text-14px font-400 line-height-26px v-html="article.content" />

          <div
            v-if="article.preAndNext"
            w-full flex b-rd-8px p-12px text-14px font-400
            :class="`bg-[#f7f7f7] ${article.preAndNext.previous ? 'justify-between' : 'justify-end'}`"
          >
            <div v-if="article.preAndNext.previous" cursor-pointer class="hover:text-[var(--color-2)]" @click="onPreOrNextPage(article.preAndNext.previous.id)">
              <span inline-flex items-center line-height-14px><i class="ds-icon-icon-left-double" text-10px /> 上一篇</span>
              <h4 mt-10px max-w-380px line-height-20px ellipsis>
                {{ article.preAndNext.previous.title }}
              </h4>
            </div>
            <div v-if="article.preAndNext.next" cursor-pointer text-right class="hover:text-[var(--color-2)]" @click="onPreOrNextPage(article.preAndNext.next.id)">
              <span inline-flex items-center line-height-14px>下一篇 <i class="ds-icon-icon-right-double" text-10px /></span>
              <h4 mt-10px max-w-380px line-height-20px ellipsis>
                {{ article.preAndNext.next.title }}
              </h4>
            </div>
          </div>

          <footer w-full b-width-1px b-rd-16px px-16px py-12px class="b-[var(--color-grey-3)]">
            <ElSpace :size="8">
              <img h-100px w-100px src="~/assets/img/gloda-sub-qrcode.png" alt="Gloda公众号">
              <section>
                <p text-16px font-500 line-height-14px class="text-[var(--color-1)]">
                  GlodaStory微信公众号
                </p>
                <p mt-12px text-14px font-400 class="text-[var(--color-grey-1)]">
                  TikTok领先的数据分析工具，更多粉丝福利扫描二维码关注
                </p>
              </section>
            </ElSpace>
          </footer>
        </ElSpace>
      </template>

      <template v-else>
        <img mx-auto h-100px w-134px src="~/assets/img/empty.svg" alt="暂无数据">
        <p pt-8px text-center text-14px font-400 class="text-[var(--color-grey-1)]">
          暂无数据
        </p>
      </template>
    </section>
  </div>
</template>

<style scoped lang="scss">
.article {
  &:deep(p) {
    min-height: 26px;
    color: var(--color-grey-1);
    font-size: 14px;
    font-weight: 400;
    line-height: 26px;
  }

  &:deep(img) {
    margin: 12px auto;
  }

  &:deep(u) {
    color: var(--color-2);
    text-decoration: none;
  }

  &:deep(h3) {
    color: var(--title-text-color);
    font-size: 16px;
    font-weight: 500;
    line-height: 26px;
  }

  &:deep(a) {
    color: var(--color-2);
    text-decoration: underline;
  }
}
</style>
